import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { toast } from 'sonner';
import { Plus, Trash2, Copy, User, Users, Search, CreditCard } from 'lucide-react';
import api from '../../lib/api';

// Enumerate the inclusive dates between start_date and end_date so admins can
// tick the days a pro-rated student is actually attending. Dates are stored as
// ISO YYYY-MM-DD strings — matches what the backend `attending_dates` expects.
const enumerateCampDates = (bootcamp) => {
  if (!bootcamp?.start_date || !bootcamp?.end_date) return [];
  const out = [];
  const s = new Date(bootcamp.start_date);
  const e = new Date(bootcamp.end_date);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

const humanDate = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
};

const blankStudent = () => ({
  name: '',
  age: '',
  medical: '',
  proRated: false,       // toggle — off = full camp
  attendingDates: [],    // only used when proRated=true
});

const ManualBootcampBookingModal = ({ open, onClose, bootcamps, defaultBootcampId, onCreated }) => {
  const [bootcampId, setBootcampId] = useState(defaultBootcampId || '');
  const [parent, setParent] = useState({
    name: '', email: '', phone: '',
    emergencyContactName: '', emergencyContactPhone: '',
  });
  const [students, setStudents] = useState([blankStudent()]);
  const [paymentMode, setPaymentMode] = useState('pending');
  const [paymentNote, setPaymentNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [pickerQuery, setPickerQuery] = useState('');
  const [selectedFamilyEmail, setSelectedFamilyEmail] = useState(null);
  const [attachedCard, setAttachedCard] = useState(null); // null=nothing picked, false=no card, {brand,last4}=card

  // Reset the form whenever the dialog re-opens.
  useEffect(() => {
    if (open) {
      setBootcampId(defaultBootcampId || '');
      setParent({ name: '', email: '', phone: '', emergencyContactName: '', emergencyContactPhone: '' });
      setStudents([blankStudent()]);
      setPaymentMode('pending');
      setPaymentNote('');
      setResult(null);
      setPickerQuery('');
      setSelectedFamilyEmail(null);
      setAttachedCard(null);
      api.get('/bootcamps/admin/student-directory')
        .then((res) => setDirectory(res.data || []))
        .catch(() => {});
    }
  }, [open, defaultBootcampId]);

  const bootcamp = useMemo(
    () => bootcamps.find((b) => b.id === bootcampId) || null,
    [bootcampId, bootcamps]
  );
  const campDates = useMemo(() => enumerateCampDates(bootcamp), [bootcamp]);
  const totalDays = campDates.length || 1;
  const perDay = bootcamp ? Math.round(((bootcamp.price || 0) / totalDays) * 100) / 100 : 0;

  // Preview each student's amount so the admin sees exactly what they'll be
  // charged BEFORE hitting submit. Mirrors the backend maths line-for-line.
  const preview = students.map((s, idx) => {
    if (!bootcamp) return { base: 0, discount: 0, final: 0, days: totalDays };
    const daysAttending = s.proRated && s.attendingDates.length > 0
      ? s.attendingDates.length
      : totalDays;
    const base = s.proRated && daysAttending < totalDays
      ? Math.round(perDay * daysAttending * 100) / 100
      : bootcamp.price || 0;
    const isSibling = idx > 0 && students.length > 1;
    const discount = isSibling ? Math.round(base * 0.30 * 100) / 100 : 0;
    const final = Math.max(0, Math.round((base - discount) * 100) / 100);
    return { base, discount, final, days: daysAttending, isSibling };
  });
  const groupTotal = preview.reduce((sum, p) => sum + p.final, 0);

  const updateStudent = (idx, patch) => {
    setStudents((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const toggleDate = (idx, iso) => {
    setStudents((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const already = s.attendingDates.includes(iso);
      return {
        ...s,
        attendingDates: already
          ? s.attendingDates.filter((d) => d !== iso)
          : [...s.attendingDates, iso].sort(),
      };
    }));
  };
  const addSibling = () => setStudents((prev) => [...prev, blankStudent()]);
  const removeStudent = (idx) => setStudents((prev) => prev.filter((_, i) => i !== idx));

  // ---- Existing-student picker ----
  const pickerQ = pickerQuery.trim().toLowerCase();
  const pickerMatches = pickerQ
    ? directory.filter((d) =>
        [d.student_name, d.parent_name, d.parent_email].some((v) => (v || '').toLowerCase().includes(pickerQ))
      ).slice(0, 8)
    : [];
  const siblingSuggestions = selectedFamilyEmail
    ? directory.filter(
        (d) =>
          (d.parent_email || '').toLowerCase() === selectedFamilyEmail.toLowerCase() &&
          !students.some((s) => s.name.trim().toLowerCase() === (d.student_name || '').trim().toLowerCase())
      )
    : [];

  const pickStudent = (st) => {
    setParent((prev) => ({
      ...prev,
      name: st.parent_name || prev.name,
      email: st.parent_email || prev.email,
      phone: st.parent_phone || prev.phone,
    }));
    const newStudent = {
      ...blankStudent(),
      name: st.student_name || '',
      age: st.age != null ? String(st.age) : '',
      medical: st.medical || '',
    };
    setStudents((prev) => {
      const names = prev.map((s) => s.name.trim().toLowerCase());
      if (names.includes((st.student_name || '').trim().toLowerCase())) return prev;
      if (prev.length === 1 && !prev[0].name.trim()) return [newStudent];
      return [...prev, newStudent];
    });
    setSelectedFamilyEmail(st.parent_email || null);
    setAttachedCard(st.has_card ? { brand: st.card_brand, last4: st.card_last4 } : false);
    setPickerQuery('');
    toast.success(`${st.student_name} added${st.has_card ? ' — saved card will be attached' : ''}`);
  };

  const validate = () => {
    if (!bootcampId) return 'Please pick a bootcamp';
    if (!parent.name.trim()) return "Please enter the parent's name";
    if (!parent.email.trim() || !parent.email.includes('@')) return "Please enter a valid parent email";
    if (!parent.phone.trim()) return "Please enter the parent's phone number";
    if (students.length === 0) return 'Please add at least one student';
    for (let i = 0; i < students.length; i++) {
      const s = students[i];
      if (!s.name.trim()) return `Please enter a name for student ${i + 1}`;
      if (s.proRated && s.attendingDates.length === 0) {
        return `${s.name || `Student ${i + 1}`}: pick at least one attending day, or turn off pro-rate`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        bootcamp_id: bootcampId,
        parent: {
          name: parent.name.trim(),
          email: parent.email.trim(),
          phone: parent.phone.trim(),
          emergency_contact_name: parent.emergencyContactName.trim(),
          emergency_contact_phone: parent.emergencyContactPhone.trim(),
        },
        students: students.map((s) => ({
          name: s.name.trim(),
          age: s.age === '' ? null : Number(s.age),
          medical: s.medical.trim(),
          attending_dates: s.proRated && s.attendingDates.length ? s.attendingDates : null,
        })),
        payment_mode: paymentMode,
        payment_note: paymentNote.trim(),
      };
      const res = await api.post('/bootcamps/admin/manual-book', payload);
      const data = res.data;
      setResult(data);
      toast.success(`Booked ${data.created.length} student${data.created.length === 1 ? '' : 's'} — total £${data.group_total.toFixed(2)}`);
      if (typeof onCreated === 'function') onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const closeAll = () => { setResult(null); onClose && onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAll(); }}>
      <DialogContent
        className="bg-neutral-950 border-neutral-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="manual-bootcamp-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white text-xl">
            {result ? 'Booking created' : 'Add student to a bootcamp'}
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 py-2" data-testid="manual-bootcamp-result">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-sm">
              <p className="font-semibold text-emerald-300 mb-2">
                {result.created.length} booking{result.created.length === 1 ? '' : 's'} created
                {result.sibling_group_id ? ' (linked as siblings)' : ''}
              </p>
              <ul className="space-y-1 text-white/80">
                {result.created.map((c) => (
                  <li key={c.id} className="flex justify-between gap-4">
                    <span>
                      {c.student_name}
                      {c.is_pro_rated ? <span className="text-amber-400 text-xs ml-2">pro-rated</span> : null}
                      {c.sibling_discount_info ? <span className="text-pink-400 text-xs ml-2">−30% sibling</span> : null}
                    </span>
                    <span className="tabular-nums font-medium">£{c.amount.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-emerald-500/30 mt-3 pt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span className="tabular-nums">£{result.group_total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-white/50 mt-2">
                Payment mode: <span className="text-white/80">{result.payment_mode}</span>
              </p>
              {result.card_attached && (
                <p className="text-xs text-emerald-300 mt-1 flex items-center gap-1" data-testid="result-card-attached">
                  <CreditCard className="w-3 h-3" />
                  Saved card {result.card_last4 ? `${(result.card_brand || 'card').toUpperCase()} •••• ${result.card_last4} ` : ''}attached — charge it from Take Payments
                </p>
              )}
            </div>

            {result.payment_link && (
              <div className="space-y-2" data-testid="manual-bootcamp-payment-link-block">
                <Label className="text-white">Payment link — send this to the parent</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={result.payment_link}
                    className="bg-neutral-900 border-neutral-700 text-white font-mono text-xs"
                    data-testid="manual-bootcamp-payment-link"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-neutral-700 text-white shrink-0"
                    onClick={() => {
                      const link = result.payment_link;
                      const done = () => toast.success('Copied to clipboard');
                      // navigator.clipboard throws in sandboxed / non-HTTPS
                      // dev contexts; fall back to a hidden textarea + execCommand.
                      try {
                        if (navigator.clipboard && window.isSecureContext) {
                          navigator.clipboard.writeText(link).then(done).catch(() => {
                            const ta = document.createElement('textarea');
                            ta.value = link; document.body.appendChild(ta); ta.select();
                            try { document.execCommand('copy'); done(); }
                            finally { document.body.removeChild(ta); }
                          });
                          return;
                        }
                      } catch { /* fall through */ }
                      const ta = document.createElement('textarea');
                      ta.value = link; document.body.appendChild(ta); ta.select();
                      try { document.execCommand('copy'); done(); }
                      finally { document.body.removeChild(ta); }
                    }}
                    data-testid="manual-bootcamp-copy-link-btn"
                  >
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                onClick={closeAll}
                className="bg-purple-500 hover:bg-purple-600 text-white"
                data-testid="manual-bootcamp-done-btn"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-6 py-2">
            {/* Bootcamp picker */}
            <div>
              <Label className="text-white mb-2 block">Bootcamp</Label>
              <Select value={bootcampId} onValueChange={setBootcampId}>
                <SelectTrigger className="bg-neutral-900 border-neutral-700 text-white" data-testid="manual-bootcamp-picker">
                  <SelectValue placeholder="Pick a bootcamp" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-700 text-white">
                  {bootcamps.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name} — £{b.price} · {b.start_date}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bootcamp && (
                <p className="text-xs text-white/50 mt-1">
                  {totalDays} day{totalDays === 1 ? '' : 's'} · £{perDay.toFixed(2)}/day if pro-rated
                </p>
              )}
            </div>

            {/* Existing student picker */}
            <div className="space-y-2 bg-neutral-900/50 border border-neutral-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-white/80 text-sm font-semibold">
                <Search className="w-4 h-4" /> Add existing student
              </div>
              <div className="relative">
                <Input
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Search current students by name or parent — auto-fills details & attaches their card"
                  className="bg-neutral-900 border-neutral-700 text-white"
                  data-testid="existing-student-search"
                />
                {pickerQ && (
                  <div className="absolute z-50 mt-1 w-full bg-neutral-900 border border-neutral-700 rounded-md max-h-56 overflow-y-auto shadow-xl">
                    {pickerMatches.length === 0 ? (
                      <div className="p-3 text-xs text-white/40">No matching students</div>
                    ) : (
                      pickerMatches.map((st) => (
                        <button
                          key={st.student_id}
                          type="button"
                          onClick={() => pickStudent(st)}
                          className="w-full text-left px-3 py-2 hover:bg-neutral-800 flex items-center justify-between gap-2"
                          data-testid={`pick-student-${st.student_id}`}
                        >
                          <span className="text-sm text-white truncate">
                            {st.student_name}{st.age != null ? ` (${st.age})` : ''}
                            <span className="text-white/40 text-xs ml-2">{st.parent_name}</span>
                          </span>
                          {st.has_card ? (
                            <span className="text-[10px] text-emerald-300 border border-emerald-500/30 rounded px-1.5 py-0.5 shrink-0">
                              {st.card_last4 ? `${(st.card_brand || 'CARD').toUpperCase()} ••${st.card_last4}` : 'Card on file'}
                            </span>
                          ) : (
                            <span className="text-[10px] text-white/30 shrink-0">No card</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              {attachedCard && (
                <div
                  className="flex items-center gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2"
                  data-testid="card-attach-banner"
                >
                  <CreditCard className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    {attachedCard.last4
                      ? `${(attachedCard.brand || 'card').toUpperCase()} •••• ${attachedCard.last4}`
                      : 'Parent has a saved card'}{' '}
                    — it will be attached to this booking so you can charge it from Take Payments
                  </span>
                </div>
              )}
              {attachedCard === false && (
                <p className="text-xs text-white/40" data-testid="no-card-note">
                  No saved card for this parent — you can still generate a payment link below.
                </p>
              )}
              {siblingSuggestions.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="sibling-suggestions">
                  <span className="text-xs text-white/50">Siblings:</span>
                  {siblingSuggestions.map((d) => (
                    <button
                      key={d.student_id}
                      type="button"
                      onClick={() => pickStudent(d)}
                      className="text-xs border border-purple-500/50 text-purple-300 hover:bg-purple-500/10 rounded-full px-3 py-1"
                      data-testid={`add-sibling-${d.student_id}`}
                    >
                      + {d.student_name}{d.age != null ? ` (${d.age})` : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Parent */}
            <div className="space-y-3 bg-neutral-900/50 border border-neutral-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-white/80 text-sm font-semibold">
                <User className="w-4 h-4" /> Parent / guardian
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/70 text-xs">Full name</Label>
                  <Input
                    value={parent.name}
                    onChange={(e) => setParent({ ...parent, name: e.target.value })}
                    className="bg-neutral-900 border-neutral-700 text-white"
                    data-testid="manual-bootcamp-parent-name"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Email</Label>
                  <Input
                    type="email"
                    value={parent.email}
                    onChange={(e) => setParent({ ...parent, email: e.target.value })}
                    className="bg-neutral-900 border-neutral-700 text-white"
                    data-testid="manual-bootcamp-parent-email"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Phone</Label>
                  <Input
                    value={parent.phone}
                    onChange={(e) => setParent({ ...parent, phone: e.target.value })}
                    className="bg-neutral-900 border-neutral-700 text-white"
                    data-testid="manual-bootcamp-parent-phone"
                  />
                </div>
                <div />
                <div>
                  <Label className="text-white/70 text-xs">Emergency contact name</Label>
                  <Input
                    value={parent.emergencyContactName}
                    onChange={(e) => setParent({ ...parent, emergencyContactName: e.target.value })}
                    className="bg-neutral-900 border-neutral-700 text-white"
                    data-testid="manual-bootcamp-emergency-name"
                  />
                </div>
                <div>
                  <Label className="text-white/70 text-xs">Emergency contact phone</Label>
                  <Input
                    value={parent.emergencyContactPhone}
                    onChange={(e) => setParent({ ...parent, emergencyContactPhone: e.target.value })}
                    className="bg-neutral-900 border-neutral-700 text-white"
                    data-testid="manual-bootcamp-emergency-phone"
                  />
                </div>
              </div>
            </div>

            {/* Students */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white/80 text-sm font-semibold">
                  <Users className="w-4 h-4" /> Students ({students.length})
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSibling}
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                  data-testid="manual-bootcamp-add-sibling-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add sibling
                </Button>
              </div>

              {students.map((s, idx) => (
                <div
                  key={idx}
                  className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 space-y-3"
                  data-testid={`manual-bootcamp-student-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/70">
                      Student {idx + 1}
                      {idx > 0 && students.length > 1 && (
                        <span className="text-pink-400 ml-2">· 30% sibling discount</span>
                      )}
                    </span>
                    {students.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStudent(idx)}
                        className="text-red-400 hover:text-red-300 h-7 px-2"
                        data-testid={`manual-bootcamp-remove-student-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <Label className="text-white/70 text-xs">Full name</Label>
                      <Input
                        value={s.name}
                        onChange={(e) => updateStudent(idx, { name: e.target.value })}
                        className="bg-neutral-900 border-neutral-700 text-white"
                        data-testid={`manual-bootcamp-student-name-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-white/70 text-xs">Age</Label>
                      <Input
                        type="number"
                        min="1"
                        max="21"
                        value={s.age}
                        onChange={(e) => updateStudent(idx, { age: e.target.value })}
                        className="bg-neutral-900 border-neutral-700 text-white"
                        data-testid={`manual-bootcamp-student-age-${idx}`}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-white/70 text-xs">Medical / allergies</Label>
                    <Textarea
                      value={s.medical}
                      onChange={(e) => updateStudent(idx, { medical: e.target.value })}
                      placeholder="Any conditions we should know about (leave blank if none)"
                      className="bg-neutral-900 border-neutral-700 text-white min-h-[60px]"
                      data-testid={`manual-bootcamp-student-medical-${idx}`}
                    />
                  </div>

                  {bootcamp && totalDays > 1 && (
                    <div className="space-y-2 pt-1">
                      <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                        <Checkbox
                          checked={s.proRated}
                          onCheckedChange={(v) => updateStudent(idx, { proRated: !!v, attendingDates: !v ? [] : s.attendingDates })}
                          data-testid={`manual-bootcamp-student-prorate-${idx}`}
                        />
                        <span>Pro-rate (student only attending some days)</span>
                      </label>
                      {s.proRated && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-6" data-testid={`manual-bootcamp-student-days-${idx}`}>
                          {campDates.map((iso) => {
                            const on = s.attendingDates.includes(iso);
                            return (
                              <label
                                key={iso}
                                className={
                                  'flex items-center gap-2 border rounded-md px-2 py-1.5 text-xs cursor-pointer transition-colors ' +
                                  (on
                                    ? 'border-purple-500 bg-purple-500/10 text-white'
                                    : 'border-neutral-700 text-white/70 hover:bg-neutral-800')
                                }
                                data-testid={`manual-bootcamp-day-${idx}-${iso}`}
                              >
                                <Checkbox
                                  checked={on}
                                  onCheckedChange={() => toggleDate(idx, iso)}
                                />
                                {humanDate(iso)}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Price preview per student */}
                  {bootcamp && (
                    <div
                      className="text-xs bg-neutral-950 border border-neutral-800 rounded-md px-3 py-2 flex justify-between items-center"
                      data-testid={`manual-bootcamp-student-preview-${idx}`}
                    >
                      <div className="text-white/60">
                        {preview[idx].days < totalDays
                          ? `${preview[idx].days} of ${totalDays} days`
                          : 'Full camp'}
                        {preview[idx].isSibling && ' · sibling'}
                      </div>
                      <div className="text-white font-semibold tabular-nums">
                        £{preview[idx].final.toFixed(2)}
                        {preview[idx].discount > 0 && (
                          <span className="text-white/40 text-[10px] line-through ml-2">
                            £{preview[idx].base.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Payment mode */}
            <div className="space-y-3 bg-neutral-900/50 border border-neutral-800 rounded-lg p-4">
              <Label className="text-white/80 text-sm font-semibold">Payment</Label>
              <RadioGroup value={paymentMode} onValueChange={setPaymentMode} className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <RadioGroupItem value="paid" id="pm-paid" data-testid="manual-bootcamp-payment-paid" />
                  <div className="text-sm">
                    <div className="text-white">Mark as paid</div>
                    <div className="text-white/50 text-xs">You&apos;re taking cash/BACS/card in person — no email link.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <RadioGroupItem value="send_link" id="pm-link" data-testid="manual-bootcamp-payment-link-radio" />
                  <div className="text-sm">
                    <div className="text-white">Generate payment link</div>
                    <div className="text-white/50 text-xs">Get a checkout URL to copy or forward to the parent.</div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <RadioGroupItem value="pending" id="pm-pending" data-testid="manual-bootcamp-payment-pending" />
                  <div className="text-sm">
                    <div className="text-white">Pending — chase manually</div>
                    <div className="text-white/50 text-xs">Create the booking as unpaid; no email sent.</div>
                  </div>
                </label>
              </RadioGroup>
              <div>
                <Label className="text-white/70 text-xs">Admin note (optional)</Label>
                <Input
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="e.g. Paid £275 cash on 12 Feb 2026"
                  className="bg-neutral-900 border-neutral-700 text-white"
                  data-testid="manual-bootcamp-payment-note"
                />
              </div>
            </div>

            {/* Total */}
            {bootcamp && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 flex justify-between items-center">
                <span className="text-white font-semibold">Group total</span>
                <span className="text-white text-xl font-bold tabular-nums" data-testid="manual-bootcamp-group-total">
                  £{groupTotal.toFixed(2)}
                </span>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeAll}
                className="border-neutral-700 text-white"
                data-testid="manual-bootcamp-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-purple-500 hover:bg-purple-600 text-white"
                data-testid="manual-bootcamp-submit-btn"
              >
                {submitting ? 'Creating…' : 'Create booking'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManualBootcampBookingModal;
